/** "New flag" dialog — creates one sparse feature-flag override row.
 *
 * Feature + level selects (from the registry), a level-dependent scope picker
 * (TenantPicker for tenant — no system sentinel, system is its own level;
 * RemoteAutocomplete for OTA / tour / notification; TagConditionInput +
 * TagSourcesSelect for tag), the flag value and an optional comment.
 * `enabled` defaults to FALSE — rows exist to override the enabled-by-default,
 * and creating a disable row is the common case — EXCEPT level=tag, where the
 * row is a condition and enabled=true means "enforced from the start".
 */
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as nffApi from '@/api/notificationFeatures';
import type { FlagChannel, FlagPolarity } from '@/api/notificationFeatures';
import type {
  FeatureFlagLevel,
  FeatureLevelDef,
  NotificationFeatureDef,
  TagSourceDef,
} from '@/api/notificationFeatures';
import * as nfApi from '@/api/notifications';
import * as otasApi from '@/api/otas';
import type { OTAListItem } from '@/api/otas';
import * as toursApi from '@/api/tours';
import type { TourListItem } from '@/api/tours';
import { AppDialog } from '@/components/AppDialog';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { HelpLink } from '@/components/HelpLink';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';
import {
  EMPTY_TAG_CONDITION,
  TagConditionInput,
  type TagConditionValue,
} from '@/components/TagConditionInput';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useScopedTenantState } from '@/scope/useScopedTenantState';

import { SYSTEM_TENANT } from '../notifications/CreateNotificationDialog';
import { FLAG_HELP } from './fieldHelp';
import { TagFlagScopeFields } from './TagFlagScopeFields';
import { tagConditionFetchers } from './tagConditionFetchers';
import { toCreateTags } from './tagFlagUtils';
import { TagSourcesSelect } from './TagSourcesSelect';

interface NotificationOption {
  id: number;
  name: string;
  /** Owning tenant code/name; null = system notification. */
  tenantLabel: string | null;
}

interface Props {
  open: boolean;
  featureDefs: NotificationFeatureDef[];
  levels: FeatureLevelDef[];
  /** Registry tag sources — options for the level=tag sources select. */
  tagSources: TagSourceDef[];
  onClose: () => void;
}

export function CreateFeatureFlagDialog({ open, featureDefs, levels, tagSources, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const [feature, setFeature] = useState('');
  const [level, setLevel] = useState<FeatureFlagLevel>('tenant');
  const [enabled, setEnabled] = useState(false);
  const [comment, setComment] = useState('');
  // Scope pickers (controlled state outside RHF, per the form-dialog pattern).
  // `tenant` is the SCOPE for level=tenant; for level=tour it is a REQUIRED
  // narrowing filter (tours are picked within one operator); for
  // level=notification an OPTIONAL one. It is never sent for those levels —
  // the tour/notification implies its owner.
  const [tenant, setTenant] = useScopedTenantState();
  const [ota, setOta] = useState<OTAListItem | null>(null);
  const [tour, setTour] = useState<TourListItem | null>(null);
  const [notification, setNotification] = useState<NotificationOption | null>(null);
  // Tag-level state: the condition (chips + match mode) and the sources.
  const [tagCondition, setTagCondition] = useState<TagConditionValue>(EMPTY_TAG_CONDITION);
  const [tagSourcesSel, setTagSourcesSel] = useState<string[]>([]);

  const onLevelChange = (next: FeatureFlagLevel) => {
    setLevel(next);
    // A deliberate clear, not a form reset: the previous level's tenant must
    // not survive into the new one (and must not come back from the scope).
    setTenant(null);
    setOta(null);
    setTour(null);
    setNotification(null);
    setTagCondition(EMPTY_TAG_CONDITION);
    // Sources default to ALL registry codenames — the largest union is the
    // least surprising suppression behavior.
    setTagSourcesSel(next === 'tag' ? tagSources.map((s) => s.codename) : []);
    // Tag rows invert the default: enabled=true = condition enforced. The
    // on/off levels keep the disable-row default.
    setEnabled(next === 'tag');
    setPolarity('require');
    setChannel('');
    setTagTenant(null);
  };

  // level=tag scope: polarity/channel/tenant. `tagTenant` is separate from the
  // `tenant` used by the tenant/tour/notification levels — on a tag row it is
  // the CONDITION's scope, not the level's ref, and sharing one piece of state
  // would leak a selection across a level switch.
  const [polarity, setPolarity] = useState<FlagPolarity>('require');
  const [channel, setChannel] = useState<FlagChannel>('');
  const [tagTenant, setTagTenant] = useState<TenantPickerValue | null>(null);

  const onTenantChange = (t: TenantPickerValue | null) => {
    setTenant(t);
    // The dependent pickers are scoped by tenant — a stale selection from
    // another operator must not survive the switch.
    setTour(null);
    setNotification(null);
  };

  const scopeComplete =
    level === 'system' ||
    (level === 'tenant' && tenant !== null) ||
    (level === 'ota' && ota !== null) ||
    (level === 'tour' && tour !== null) ||
    (level === 'notification' && notification !== null) ||
    (level === 'tag' && tagCondition.chips.length > 0 && tagSourcesSel.length > 0);

  const createMutation = useMutation({
    mutationFn: () =>
      nffApi.create({
        feature,
        level,
        ...(level === 'tenant' ? { tour_operator: tenant!.id } : {}),
        ...(level === 'ota' ? { ota: ota!.id } : {}),
        ...(level === 'tour' ? { tour: tour!.id } : {}),
        ...(level === 'notification' ? { notification: notification!.id } : {}),
        ...(level === 'tag'
          ? {
              tags: toCreateTags(tagCondition),
              tag_match_mode: tagCondition.matchMode,
              tag_sources: tagSourcesSel,
              polarity,
              channel,
              ...(tagTenant ? { tour_operator: tagTenant.id } : {}),
            }
          : {}),
        enabled,
        comment: comment.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-feature-flags'] });
      snackbar.show('Feature flag created.', 'success');
      onClose();
    },
    onError: (e) =>
      snackbar.show(
        e instanceof ConsoleApiError ? e.message : 'Could not create the flag.',
        'error',
      ),
  });

  const canSubmit = feature !== '' && scopeComplete && !createMutation.isPending;

  const hasPinnedChip = tagCondition.chips.some((c) => 'tag_id' in c.spec);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={
        <>
          New feature flag <HelpLink story="notification-feature-flags" newTab />
        </>
      }
      // The tag-condition widget needs elbow room for its chips.
      size={level === 'tag' ? 'md' : 'sm'}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={!canSubmit} onClick={() => createMutation.mutate()}>
            Create
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            select
            required
            size="small"
            label="Feature"
            value={feature}
            onChange={(e) => setFeature(e.target.value)}
            sx={{ flexGrow: 1 }}
          >
            {featureDefs.map((f) => (
              <MenuItem key={f.codename} value={f.codename}>
                {f.name}{' '}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace', ml: 0.5 }}
                >
                  {f.codename}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          <FieldHelp help={FLAG_HELP.feature} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            select
            required
            size="small"
            label="Level"
            value={level}
            onChange={(e) => onLevelChange(e.target.value as FeatureFlagLevel)}
            sx={{ flexGrow: 1 }}
            helperText={
              level === 'system'
                ? 'Applies everywhere. Any more-specific flag still applies on top.'
                : undefined
            }
          >
            {levels.map((l) => (
              <MenuItem key={l.codename} value={l.codename}>
                {l.name}
              </MenuItem>
            ))}
          </TextField>
          <FieldHelp help={FLAG_HELP.level} />
        </Box>

        {level === 'tenant' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ flexGrow: 1 }}>
              <TenantPicker value={tenant} onChange={setTenant} label="Tenant" />
            </Box>
            <FieldHelp help={FLAG_HELP.scope} />
          </Box>
        )}
        {level === 'ota' && (
          <RemoteAutocomplete<OTAListItem>
            label="OTA"
            size="small"
            fetchOnEmpty
            value={ota}
            getOptionLabel={(o) => o.name || `#${o.id}`}
            getOptionKey={(o) => o.id}
            queryFn={async (q) => (await otasApi.list({ search: q, pageSize: 20 })).results}
            onChange={(v) => setOta((v as OTAListItem) ?? null)}
          />
        )}
        {level === 'tour' && (
          <>
            <TenantPicker value={tenant} onChange={onTenantChange} label="Tenant (owns the tour)" />
            {tenant ? (
              <RemoteAutocomplete<TourListItem>
                label="Tour"
                size="small"
                fetchOnEmpty
                value={tour}
                getOptionLabel={(t) => `${t.name} #${t.id}`}
                getOptionKey={(t) => t.id}
                queryFn={async (q) =>
                  (await toursApi.list({ search: q, tour_operator_id: tenant.id, pageSize: 20 }))
                    .results
                }
                onChange={(v) => setTour((v as TourListItem) ?? null)}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Pick the tenant first — tours are chosen within one operator.
              </Typography>
            )}
          </>
        )}
        {level === 'notification' && (
          <>
            <TenantPicker
              value={tenant}
              onChange={onTenantChange}
              label="Tenant filter (optional)"
              extraOptions={[SYSTEM_TENANT]}
            />
            <RemoteAutocomplete<NotificationOption>
              label="Notification"
              size="small"
              fetchOnEmpty
              value={notification}
              getOptionLabel={(n) => `${n.name} #${n.id}`}
              getOptionKey={(n) => n.id}
              renderOption={(props, n) => (
                <li {...props} key={n.id}>
                  {n.name} #{n.id}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 0.75 }}
                  >
                    [{n.tenantLabel ?? 'system'}]
                  </Typography>
                </li>
              )}
              queryFn={async (q) =>
                (
                  await nfApi.list({
                    page_size: 20,
                    ...(tenant
                      ? { tour_operator_id: tenant.id === SYSTEM_TENANT.id ? 'system' : tenant.id }
                      : {}),
                    filters: q ? [{ namespace: 'name', operator: 'icontains', value: q }] : [],
                  })
                ).results.map((n) => ({
                  id: n.id,
                  name: n.name,
                  tenantLabel: n.tenant ? n.tenant.code || n.tenant.name : null,
                }))
              }
              onChange={(v) => setNotification((v as NotificationOption) ?? null)}
            />
          </>
        )}
        {level === 'tag' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <TagConditionInput
                  value={tagCondition}
                  onChange={setTagCondition}
                  fetchers={tagConditionFetchers}
                  label="Tag condition"
                  helperText="Blocks with this feature render only when these tags are present in the rendering context."
                />
              </Box>
              <FieldHelp help={FLAG_HELP.tag_condition} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TagSourcesSelect
                value={tagSourcesSel}
                onChange={setTagSourcesSel}
                options={tagSources}
              />
              <FieldHelp help={FLAG_HELP.tag_sources} />
            </Box>
            <TagFlagScopeFields
              polarity={polarity}
              onPolarityChange={setPolarity}
              channel={channel}
              onChannelChange={setChannel}
              tenant={tagTenant}
              onTenantChange={setTagTenant}
              chipCount={tagCondition.chips.length}
            />
            {hasPinnedChip && (
              <Typography variant="caption" color="info.main">
                A tenant-pinned tag (#) never matches renders of other tenants — in AND mode it
                makes the feature unrenderable everywhere else. Use a system-wide chip or OR mode if
                that is not intended.
              </Typography>
            )}
          </>
        )}

        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label={labelWithHelp(enabled ? 'Enabled' : 'Disabled', FLAG_HELP.enabled)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
          {level === 'tag'
            ? 'Enabled = the condition is enforced (blocks render only with the tags present). Disabled = suspended — the condition is ignored until re-enabled.'
            : 'Without any flag row the feature is enabled — create a Disabled row to turn it off for this scope.'}
        </Typography>

        <TextField
          size="small"
          label="Comment (optional)"
          multiline
          minRows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          InputProps={{ endAdornment: <FieldHelp help={FLAG_HELP.comment} /> }}
        />
      </Box>
    </AppDialog>
  );
}
